import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform, ActivityIndicator } from 'react-native';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import DashboardSidebar from '../components/DashboardSidebar';
import AnimatedCard from '../components/AnimatedCard';
import { calendarService } from '../services/calendarService';

const CalendarScreen = () => {
  const { palette, mode } = useTheme();
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(false);

  // Get calendar data for current month
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Calculate calendar grid
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Build calendar days array
  const buildCalendarDays = () => {
    const days = [];
    
    // Previous month days
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, daysInPrevMonth - i),
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        date: new Date(year, month, i),
      });
    }
    
    // Calculate minimum rows needed (either 5 or 6)
    const totalDaysSoFar = days.length;
    const rowsNeeded = Math.ceil(totalDaysSoFar / 7);
    const totalCells = rowsNeeded * 7;
    
    // Next month days to complete the grid
    const remainingDays = totalCells - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        date: new Date(year, month + 1, i),
      });
    }
    
    return days;
  };

  const calendarDays = buildCalendarDays();

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Check if date is selected
  const isSelected = (date) => {
    if (!selectedDate) return false;
    return date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    const dateKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    return events[dateKey] || [];
  };

  // Handle day click
  const handleDayClick = (dayObj) => {
    setSelectedDate(dayObj.date);
  };

  // Load events from Firestore
  useEffect(() => {
    if (!user) return;
    
    const loadEvents = async () => {
      setLoading(true);
      const month = currentDate.getMonth();
      const year = currentDate.getFullYear();
      
      const { events: eventsList, error } = await calendarService.getEventsByMonth(user.uid, month, year);
      
      if (!error && eventsList) {
        // Group events by date for easy lookup
        const eventsMap = {};
        eventsList.forEach(event => {
          const dateKey = `${event.date.getFullYear()}-${event.date.getMonth() + 1}-${event.date.getDate()}`;
          if (!eventsMap[dateKey]) {
            eventsMap[dateKey] = [];
          }
          eventsMap[dateKey].push(event);
        });
        setEvents(eventsMap);
      } else if (error) {
        console.error('Error loading events:', error);
      }
      
      setLoading(false);
    };
    
    loadEvents();
  }, [user, currentDate]);

  return (
    <View style={styles.container}>
      <View style={styles.layoutRow}>
        {/* Sidebar */}
        <DashboardSidebar currentPage="calendar" />

        {/* Main Content */}
        <ScrollView style={styles.mainCol} contentContainerStyle={styles.contentContainer}>
          {/* Header Card - Pink style like mobile */}
          <AnimatedCard style={styles.headerCard}>
            <View style={styles.headerInner}>
              <Text style={styles.headerTitle}>My Calendar</Text>
              <TouchableOpacity
                onPress={goToToday}
                style={styles.todayButton}
              >
                <Text style={styles.todayButtonText}>Today</Text>
              </TouchableOpacity>
            </View>
          </AnimatedCard>

          {/* Calendar Controls */}
          <View style={styles.calendarControls}>
            <TouchableOpacity onPress={goToPreviousMonth} style={styles.navButton}>
              <Ionicons name="chevron-back" size={24} color="#000000" />
            </TouchableOpacity>
            <Text style={styles.monthYearText}>
              {monthNames[month]} {year}
            </Text>
            <TouchableOpacity onPress={goToNextMonth} style={styles.navButton}>
              <Ionicons name="chevron-forward" size={24} color="#000000" />
            </TouchableOpacity>
          </View>

          {loading && (
            <ActivityIndicator size="small" color={palette.primary} style={styles.loader} />
          )}

          {/* Calendar Grid Card */}
          <AnimatedCard style={styles.calendarCard}>
            {/* Day Names Header */}
            <View style={styles.dayNamesRow}>
              {dayNames.map((dayName) => (
                <View key={dayName} style={styles.dayNameCell}>
                  <Text style={styles.dayNameText}>
                    {dayName}
                  </Text>
                </View>
              ))}
            </View>

            {/* Calendar Days Grid */}
            <View style={styles.daysGrid}>
              {calendarDays.map((dayObj, index) => {
                const dayEvents = getEventsForDate(dayObj.date);
                const hasEvents = dayEvents.length > 0;
                const isTodayDate = isToday(dayObj.date);
                const isSelectedDate = isSelected(dayObj.date);

                return (
                  <View key={index} style={styles.dayCellWrapper}>
                    <TouchableOpacity
                      onPress={() => handleDayClick(dayObj)}
                      style={[
                        styles.dayCell,
                        !dayObj.isCurrentMonth && styles.dayOutOfMonth,
                        isTodayDate && styles.todayCell,
                        isSelectedDate && !isTodayDate && styles.selectedCell,
                      ]}
                      disabled={!dayObj.isCurrentMonth}
                    >
                      <Text
                        style={[
                          styles.dayNumber,
                          !dayObj.isCurrentMonth && styles.dayNumberFaded,
                          isTodayDate && styles.todayText,
                        ]}
                      >
                        {dayObj.day}
                      </Text>
                      {hasEvents && (
                        <View style={styles.eventDots}>
                          {dayEvents.slice(0, 3).map((event, idx) => (
                            <View
                              key={idx}
                              style={[
                                styles.eventDot,
                                { backgroundColor: event.type === 'live' ? '#0DC44A' : event.type === 'quiz' ? '#FFA500' : '#FF4747' }
                              ]}
                            />
                          ))}
                        </View>
                      )}
                    </TouchableOpacity>
                  </View>
                );
              })}
            </View>
          </AnimatedCard>

          {/* Events List for Selected Date */}
          {selectedDate && (
            <View style={styles.eventsSection}>
              <Text style={styles.eventsSectionTitle}>
                Events for {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </Text>
              {getEventsForDate(selectedDate).length > 0 ? (
                <View style={styles.eventsList}>
                  {getEventsForDate(selectedDate).map((event) => (
                    <AnimatedCard
                      key={event.id}
                      style={styles.eventCard}
                    >
                      <View style={styles.eventCardHeader}>
                        <View style={[
                          styles.eventTypeIcon,
                          { backgroundColor: event.type === 'live' ? '#0DC44A' : event.type === 'quiz' ? '#FFA500' : '#FF4747' }
                        ]}>
                          <Ionicons
                            name={event.type === 'live' ? 'videocam' : event.type === 'quiz' ? 'document-text' : 'calendar'}
                            size={20}
                            color="#FFFFFF"
                          />
                        </View>
                        <View style={styles.eventCardContent}>
                          <Text style={styles.eventCardTitle}>
                            {event.title}
                          </Text>
                          {event.time && (
                            <Text style={styles.eventCardTime}>
                              {event.time}
                            </Text>
                          )}
                          {event.description && (
                            <Text style={styles.eventCardDescription}>
                              {event.description}
                            </Text>
                          )}
                        </View>
                      </View>
                    </AnimatedCard>
                  ))}
                </View>
              ) : (
                <AnimatedCard style={styles.noEventsCard}>
                  <Text style={styles.noEventsText}>
                    No events scheduled for this day
                  </Text>
                </AnimatedCard>
              )}
            </View>
          )}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingLeft: 48,
    paddingRight: 48,
    paddingTop: 160,
    paddingBottom: 40,
    minHeight: '100vh',
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  layoutRow: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    gap: 24,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  mainCol: {
    flex: 1,
    maxWidth: 900,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  contentContainer: {
    paddingBottom: 40,
    gap: 20,
  },
  // Header Card - Pink style like mobile
  headerCard: {
    backgroundColor: '#FF6B6B',
    borderRadius: 20,
    borderWidth: 4,
    borderColor: '#000000',
    padding: 20,
    ...Platform.select({
      web: {
        boxShadow: '5px 7px 0px #000000',
      },
    }),
  },
  headerInner: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 28,
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: '700',
    color: '#FFFFFF',
    ...Platform.select({
      web: {
        textShadow: '2px 2px 0px rgba(0,0,0,0.3)',
      },
    }),
  },
  todayButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#000000',
    backgroundColor: '#FFFFFF',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        boxShadow: '3px 3px 0px #000000',
        transition: 'transform 0.1s, box-shadow 0.1s',
      },
    }),
  },
  todayButtonText: {
    fontSize: 14,
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: '700',
    color: '#000000',
  },
  // Calendar Controls
  calendarControls: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  navButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 3,
    borderColor: '#000000',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        boxShadow: '3px 3px 0px rgba(0,0,0,0.5)',
        transition: 'transform 0.1s, box-shadow 0.1s',
      },
    }),
  },
  monthYearText: {
    fontSize: 26,
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: '700',
    color: '#000000',
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.2)',
      },
    }),
  },
  loader: {
    marginVertical: 10,
  },
  // Calendar Card
  calendarCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    borderWidth: 4,
    borderColor: '#000000',
    padding: 16,
    ...Platform.select({
      web: {
        boxShadow: '5px 7px 0px #000000',
      },
    }),
  },
  dayNamesRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  dayNameCell: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 12,
  },
  dayNameText: {
    fontSize: 13,
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: '600',
    textTransform: 'uppercase',
    color: '#888888',
  },
  daysGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  dayCellWrapper: {
    width: `${100 / 7}%`,
    aspectRatio: 1,
    padding: 3,
  },
  dayCell: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'background-color 0.15s, transform 0.1s',
      },
    }),
  },
  dayOutOfMonth: {
    opacity: 0.3,
  },
  todayCell: {
    backgroundColor: '#FF4747',
  },
  selectedCell: {
    backgroundColor: '#F7DF7B',
    borderWidth: 2,
    borderColor: '#000000',
  },
  dayNumber: {
    fontSize: 16,
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: '600',
    color: '#000000',
  },
  dayNumberFaded: {
    color: 'rgba(0,0,0,0.3)',
  },
  todayText: {
    color: '#FFFFFF',
    fontWeight: '700',
  },
  eventDots: {
    flexDirection: 'row',
    gap: 3,
    position: 'absolute',
    bottom: 6,
  },
  eventDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  // Events Section
  eventsSection: {
    marginTop: 8,
  },
  eventsSectionTitle: {
    fontSize: 22,
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: '700',
    marginBottom: 16,
    color: '#000000',
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.15)',
      },
    }),
  },
  eventsList: {
    gap: 12,
  },
  eventCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    borderWidth: 3,
    borderColor: '#000000',
    padding: 16,
    ...Platform.select({
      web: {
        boxShadow: '4px 4px 0px rgba(0,0,0,0.5)',
      },
    }),
  },
  eventCardHeader: {
    flexDirection: 'row',
    gap: 14,
    alignItems: 'center',
  },
  eventTypeIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#000000',
  },
  eventCardContent: {
    flex: 1,
  },
  eventCardTitle: {
    fontSize: 16,
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: '700',
    marginBottom: 4,
    color: '#000000',
  },
  eventCardTime: {
    fontSize: 13,
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: '500',
    color: 'rgba(0,0,0,0.6)',
    marginBottom: 4,
  },
  eventCardDescription: {
    fontSize: 14,
    fontFamily: 'Plus Jakarta Sans',
    lineHeight: 20,
    color: 'rgba(0,0,0,0.7)',
  },
  noEventsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    borderWidth: 3,
    borderColor: '#000000',
    padding: 28,
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '4px 4px 0px rgba(0,0,0,0.5)',
      },
    }),
  },
  noEventsText: {
    fontSize: 15,
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: '500',
    color: '#666666',
    textAlign: 'center',
  },
});

export default CalendarScreen;
